/** Detect unsaved wizard changes vs the last persisted review. */

import type { AgentRun, ArtefactRatings, PhaseRatings, ReviewRecord } from "./types";
import { artefactRatingsComplete } from "./ArtefactsPhase";
import { artefactRatingsFromReview, phaseRatingsFromReview } from "./reviewHydration";

function ratingSnapshot(rating: {
  answers: Record<string, string>;
  check_comments: Record<string, string>;
  suggested_rag: string | null;
  human_rag: string | null;
  human_note: string;
  human_approval?: string | null;
}) {
  return JSON.stringify(rating);
}

export function persistedArtefactsComplete(run: AgentRun, saved: ReviewRecord | null): boolean {
  const artefacts = run.artefacts ?? [];
  if (!artefacts.length || !run.artefact_review_criteria) return true;
  if (!saved) return false;
  return artefactRatingsComplete(run, artefactRatingsFromReview(saved));
}

export function reviewIsDirty(
  run: AgentRun,
  saved: ReviewRecord | null,
  ratings: PhaseRatings,
  artefactRatings: ArtefactRatings,
): boolean {
  if (!saved) return false;

  const savedPhases = phaseRatingsFromReview(saved);
  if (ratingSnapshot(savedPhases.orchestration) !== ratingSnapshot(ratings.orchestration)) return true;
  if (ratingSnapshot(savedPhases.output) !== ratingSnapshot(ratings.output)) return true;

  const savedArtefacts = artefactRatingsFromReview(saved);
  for (const artefact of run.artefacts ?? []) {
    const current = artefactRatings[artefact.artifact_id] ?? {
      answers: {},
      check_comments: {},
      suggested_rag: null,
      human_rag: null,
      human_note: "",
      human_approval: null,
    };
    const previous = savedArtefacts[artefact.artifact_id] ?? {
      answers: {},
      check_comments: {},
      suggested_rag: null,
      human_rag: null,
      human_note: "",
      human_approval: null,
    };
    if (ratingSnapshot(current) !== ratingSnapshot(previous)) return true;
  }

  return false;
}

export function hasPartialArtefactRatings(run: AgentRun, artefactRatings: ArtefactRatings): boolean {
  for (const artefact of run.artefacts ?? []) {
    const rating = artefactRatings[artefact.artifact_id];
    if (!rating) continue;
    if (rating.human_rag !== null || Object.keys(rating.answers).length > 0) return true;
  }
  return false;
}

export function reviewNeedsSave(
  run: AgentRun,
  saved: ReviewRecord | null,
  ratings: PhaseRatings,
  artefactRatings: ArtefactRatings,
  artefactsDone: boolean,
  submittedInSession: boolean,
): boolean {
  if (!saved) return !submittedInSession;
  if (reviewIsDirty(run, saved, ratings, artefactRatings)) return true;
  return artefactsDone && !persistedArtefactsComplete(run, saved);
}
